import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import { authClient } from "@/lib/auth-client";
import { type AuthRole, hasAuthRole, isConsumerSession } from "@/lib/auth-roles";

type ProtectedRouteProps = {
  children: ReactNode;
  allowedRoles?: AuthRole[];
  signInPath?: string;
  unauthorizedPath?: string;
};

function hasAllowedRole(role: unknown, allowedRoles: AuthRole[] | undefined): boolean {
  if (!allowedRoles) {
    return true;
  }

  return allowedRoles.some((allowedRole) => hasAuthRole(role, allowedRole));
}

export function ProtectedRoute({
  children,
  allowedRoles,
  signInPath = "/auth/restaurant/sign-in",
  unauthorizedPath,
}: ProtectedRouteProps) {
  const { data, isPending } = authClient.useSession();
  const navigate = useNavigate();
  const authorized = Boolean(data && hasAllowedRole(data.user.role, allowedRoles));
  const fallbackPath = unauthorizedPath ?? (isConsumerSession(data) ? "/account" : "/");

  useEffect(() => {
    if (!(isPending || data)) {
      navigate(signInPath);
    } else if (!isPending && data && !authorized) {
      navigate(fallbackPath);
    }
  }, [authorized, data, fallbackPath, isPending, navigate, signInPath]);

  if (isPending) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (data && authorized) {
    return <>{children}</>;
  }

  return null;
}
